import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { FaEdit, FaTrash, FaPlus, FaTimes } from "react-icons/fa";

import API_BASE_URL from "../config/api";

const Testimonials = () => {

    // =========================================================
    // STATES
    // =========================================================

    const [testimonials, setTestimonials] = useState([]);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [showForm, setShowForm] = useState(false);

    const [editId, setEditId] = useState(null);

    const [imagePreview, setImagePreview] = useState(null);

    const [formData, setFormData] = useState({
        title: "",
        designation: "",
        description: "",
        sort_order: "1",
        image: null
    });


    // =========================================================
    // FETCH TESTIMONIALS
    // =========================================================

    const fetchTestimonials = async () => {

        try {

            setLoading(true);

            const response = await fetch(
                `${API_BASE_URL}/admin/testimonials`,
                {
                    method: "GET",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include"
                }
            );

            const data = await response.json();

            console.log("TESTIMONIAL LIST RESPONSE:", data);

            if (response.ok && data.success) {

                setTestimonials(data.testimonials || []);

            } else {

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: data.message || "Unable to fetch testimonials."
                });
            }

        } catch (error) {

            console.error("FETCH TESTIMONIAL ERROR:", error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Something went wrong while loading testimonials."
            });

        } finally {

            setLoading(false);
        }
    };


    // =========================================================
    // USE EFFECT
    // =========================================================

    useEffect(() => {

        fetchTestimonials();

    }, []);


    // =========================================================
    // INPUT CHANGE
    // =========================================================

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };


    // =========================================================
    // IMAGE CHANGE
    // =========================================================

    const handleImageChange = (e) => {

        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        // Allowed image types
        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
            "image/gif"
        ];

        if (!allowedTypes.includes(file.type)) {

            Swal.fire({
                icon: "warning",
                title: "Invalid Image",
                text: "Only JPG, JPEG, PNG, WEBP and GIF images are allowed."
            });

            e.target.value = "";

            return;
        }


        // Maximum 2 MB
        if (file.size > 2 * 1024 * 1024) {

            Swal.fire({
                icon: "warning",
                title: "Image Too Large",
                text: "Image size must be less than 2 MB."
            });

            e.target.value = "";

            return;
        }


        setFormData((prev) => ({
            ...prev,
            image: file
        }));


        // Preview
        const previewUrl = URL.createObjectURL(file);

        setImagePreview(previewUrl);
    };


    // =========================================================
    // RESET FORM
    // =========================================================

    const resetForm = () => {

        setFormData({
            title: "",
            designation: "",
            description: "",
            sort_order: "1",
            image: null
        });

        setImagePreview(null);

        setEditId(null);

        setShowForm(false);
    };


    // =========================================================
    // OPEN ADD FORM
    // =========================================================

    const handleAdd = () => {

        setFormData({
            title: "",
            designation: "",
            description: "",
            sort_order: "1",
            image: null
        });

        setImagePreview(null);

        setEditId(null);

        setShowForm(true);
    };


    // =========================================================
    // EDIT TESTIMONIAL
    // =========================================================

    const handleEdit = async (id) => {

        try {

            setSaving(true);

            const response = await fetch(
                `${API_BASE_URL}/admin/testimonials/${id}`,
                {
                    method: "GET",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include"
                }
            );

            const data = await response.json();

            console.log("TESTIMONIAL EDIT RESPONSE:", data);

            if (!response.ok || !data.success) {

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: data.message || "Unable to fetch testimonial."
                });

                return;
            }


            const testimonial = data.testimonial;


            setFormData({
                title: testimonial.title || "",

                designation: testimonial.designation || "",

                description: testimonial.description || "",

                sort_order: testimonial.sort_order || "1",

                image: null
            });


            // Existing image preview
            if (testimonial.image_url) {

                setImagePreview(testimonial.image_url);

            } else if (testimonial.image) {

                const baseUrl = API_BASE_URL.replace("/api", "");

                setImagePreview(
                    `${baseUrl}/storage/testimonial/${encodeURIComponent(
                        testimonial.image
                    )}`
                );

            } else {

                setImagePreview(null);
            }


            setEditId(id);

            setShowForm(true);

        } catch (error) {

            console.error("EDIT TESTIMONIAL ERROR:", error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Something went wrong while loading testimonial."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // SAVE TESTIMONIAL
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();


        // =====================================================
        // VALIDATION
        // =====================================================

        if (!formData.title.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Title Required",
                text: "Please enter testimonial title."
            });

            return;
        }


        if (!formData.designation.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Designation Required",
                text: "Please enter designation."
            });

            return;
        }


        if (!formData.description.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Description Required",
                text: "Please enter testimonial description."
            });

            return;
        }


        if (!formData.sort_order.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Sort Order Required",
                text: "Please enter sort order."
            });

            return;
        }


        // Image required only while adding
        if (!editId && !formData.image) {

            Swal.fire({
                icon: "warning",
                title: "Image Required",
                text: "Please select testimonial image."
            });

            return;
        }


        try {

            setSaving(true);


            const data = new FormData();

            data.append(
                "title",
                formData.title.trim()
            );

            data.append(
                "designation",
                formData.designation.trim()
            );

            data.append(
                "description",
                formData.description
            );

            data.append(
                "sort_order",
                formData.sort_order.trim()
            );


            if (formData.image) {

                data.append(
                    "image",
                    formData.image
                );
            }


            let url = `${API_BASE_URL}/admin/testimonials`;

            let method = "POST";


            // =================================================
            // UPDATE
            // =================================================

            if (editId) {

                url = `${API_BASE_URL}/admin/testimonials/${editId}`;

                /*
                 * Laravel method spoofing
                 *
                 * We use POST + _method=PUT
                 * because image upload with PUT can create
                 * multipart/form-data issues in PHP/Laravel.
                 */

                data.append("_method", "PUT");
            }


            console.log("TESTIMONIAL FORM DATA:", {
                title: formData.title,
                designation: formData.designation,
                description: formData.description,
                sort_order: formData.sort_order,
                image: formData.image
                    ? formData.image.name
                    : null
            });


            const response = await fetch(
                url,
                {
                    method: method,

                    headers: {
                        "Accept": "application/json"
                    },

                    body: data,

                    credentials: "include"
                }
            );


            const result = await response.json();

            console.log(
                "TESTIMONIAL SAVE RESPONSE:",
                result
            );


            if (response.ok && result.success) {

                Swal.fire({
                    icon: "success",
                    title: editId
                        ? "Testimonial Updated"
                        : "Testimonial Added",
                    text: result.message,
                    timer: 1800,
                    showConfirmButton: false
                });


                resetForm();

                fetchTestimonials();

            } else {

                console.error(
                    "TESTIMONIAL SAVE ERROR RESPONSE:",
                    result
                );


                let errorMessage =
                    result.message ||
                    "Unable to save testimonial.";


                // Laravel validation errors
                if (result.errors) {

                    const validationMessages =
                        Object.values(result.errors)
                            .flat()
                            .join("\n");

                    if (validationMessages) {

                        errorMessage =
                            validationMessages;
                    }
                }


                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: errorMessage
                });
            }

        } catch (error) {

            console.error(
                "SAVE TESTIMONIAL ERROR:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Something went wrong while saving testimonial."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // DELETE TESTIMONIAL
    // =========================================================

    const handleDelete = async (id) => {

        const confirm = await Swal.fire({

            title: "Delete Testimonial?",

            text: "This testimonial and its image will be permanently deleted.",

            icon: "warning",

            showCancelButton: true,

            confirmButtonText: "Yes, Delete",

            cancelButtonText: "Cancel"
        });


        if (!confirm.isConfirmed) {
            return;
        }


        try {

            setSaving(true);


            const response = await fetch(
                `${API_BASE_URL}/admin/testimonials/${id}`,
                {
                    method: "DELETE",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include"
                }
            );


            const result = await response.json();

            console.log(
                "DELETE TESTIMONIAL RESPONSE:",
                result
            );


            if (response.ok && result.success) {

                Swal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: result.message,
                    timer: 1500,
                    showConfirmButton: false
                });


                fetchTestimonials();

            } else {

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        result.message ||
                        "Unable to delete testimonial."
                });
            }

        } catch (error) {

            console.error(
                "DELETE TESTIMONIAL ERROR:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Something went wrong while deleting testimonial."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // IMAGE URL HELPER
    // =========================================================

    const getImageUrl = (testimonial) => {

        if (testimonial.image_url) {

            return testimonial.image_url;
        }


        if (testimonial.image) {

            const baseUrl =
                API_BASE_URL.replace("/api", "");

            return `${baseUrl}/storage/testimonial/${encodeURIComponent(
                testimonial.image
            )}`;
        }


        return null;
    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            className="container-fluid py-4"
            style={{
                maxHeight: "500px",
                overflowX: "auto",
                overflowY: "auto"
            }}
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <div>

                    <h4 className="mb-1">
                        Testimonials
                    </h4>

                    <p className="text-muted mb-0">
                        Manage website testimonials
                    </p>

                </div>


                {!showForm && (

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAdd}
                    >

                        <FaPlus className="me-2" />

                        Add Testimonial

                    </button>

                )}

            </div>


            {/* =================================================
                ADD / EDIT FORM
            ================================================= */}

            {showForm && (

                <div className="card shadow-sm mb-4">

                    <div className="card-header d-flex justify-content-between align-items-center">

                        <h5 className="mb-0">

                            {editId
                                ? "Edit Testimonial"
                                : "Add Testimonial"}

                        </h5>


                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetForm}
                        >

                            <FaTimes />

                        </button>

                    </div>


                    <div className="card-body">

                        <form onSubmit={handleSubmit}>

                            <div className="row">


                                {/* TITLE */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Title
                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>

                                    <input
                                        type="text"
                                        name="title"
                                        className="form-control"
                                        value={formData.title}
                                        onChange={handleChange}
                                        maxLength={250}
                                        placeholder="Enter testimonial title"
                                    />

                                </div>


                                {/* DESIGNATION */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Designation
                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>

                                    <input
                                        type="text"
                                        name="designation"
                                        className="form-control"
                                        value={formData.designation}
                                        onChange={handleChange}
                                        maxLength={250}
                                        placeholder="Enter designation"
                                    />

                                </div>


                                {/* SORT ORDER */}

                                <div className="col-md-3 mb-3">

                                    <label className="form-label">

                                        Sort Order
                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>

                                    <input
                                        type="number"
                                        name="sort_order"
                                        className="form-control"
                                        value={formData.sort_order}
                                        onChange={handleChange}
                                        min="1"
                                        max="9999"
                                        placeholder="1"
                                    />

                                </div>


                                {/* IMAGE */}

                                <div className="col-md-9 mb-3">

                                    <label className="form-label">

                                        Image

                                        {!editId && (
                                            <span className="text-danger">
                                                *
                                            </span>
                                        )}

                                    </label>

                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".jpg,.jpeg,.png,.webp,.gif"
                                        onChange={handleImageChange}
                                    />

                                    <small className="text-muted">

                                        JPG, JPEG, PNG, WEBP or GIF.
                                        Maximum 2 MB.

                                    </small>

                                </div>


                                {/* IMAGE PREVIEW */}

                                {imagePreview && (

                                    <div className="col-12 mb-3">

                                        <label className="form-label">
                                            Image Preview
                                        </label>

                                        <div>

                                            <img
                                                src={imagePreview}
                                                alt="Testimonial Preview"
                                                style={{
                                                    width: "140px",
                                                    height: "100px",
                                                    objectFit: "cover",
                                                    borderRadius: "8px",
                                                    border: "1px solid #ddd"
                                                }}
                                            />

                                        </div>

                                    </div>

                                )}


                                {/* DESCRIPTION */}

                                <div className="col-12 mb-3">

                                    <label className="form-label">

                                        Description
                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>

                                    <textarea
                                        name="description"
                                        className="form-control"
                                        rows="5"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Enter testimonial description"
                                    />

                                </div>


                                {/* BUTTONS */}

                                <div className="col-12">

                                    <button
                                        type="submit"
                                        className="btn btn-primary me-2"
                                        disabled={saving}
                                    >

                                        {saving
                                            ? "Saving..."
                                            : editId
                                                ? "Update Testimonial"
                                                : "Add Testimonial"}

                                    </button>


                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={resetForm}
                                        disabled={saving}
                                    >

                                        Cancel

                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>

                </div>

            )}


            {/* =================================================
                TESTIMONIAL LIST
            ================================================= */}

            {!showForm && (

                <div className="card shadow-sm">

                    <div className="card-body p-0">

                        {loading ? (

                            <div className="text-center py-5">

                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                >
                                </div>

                                <p className="mt-2 mb-0">
                                    Loading testimonials...
                                </p>

                            </div>

                        ) : testimonials.length === 0 ? (

                            <div className="text-center py-5">

                                <p className="text-muted mb-3">
                                    No testimonials found.
                                </p>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleAdd}
                                >

                                    <FaPlus className="me-2" />

                                    Add Testimonial

                                </button>

                            </div>

                        ) : (

                            <div className="table-responsive">

                                <table className="table table-bordered table-hover align-middle mb-0">

                                    <thead className="table-light">

                                        <tr>

                                            <th style={{ width: "70px" }}>
                                                ID
                                            </th>

                                            <th style={{ width: "100px" }}>
                                                Image
                                            </th>

                                            <th>
                                                Title
                                            </th>

                                            <th>
                                                Designation
                                            </th>

                                            <th>
                                                Description
                                            </th>

                                            <th style={{ width: "90px" }}>
                                                Order
                                            </th>

                                            <th style={{ width: "150px" }}>
                                                Action
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {testimonials.map((testimonial) => (

                                            <tr key={testimonial.id}>

                                                {/* ID */}

                                                <td>
                                                    {testimonial.id}
                                                </td>


                                                {/* IMAGE */}

                                                <td>

                                                    {getImageUrl(testimonial) ? (

                                                        <img
                                                            src={getImageUrl(testimonial)}
                                                            alt={testimonial.title}
                                                            style={{
                                                                width: "70px",
                                                                height: "55px",
                                                                objectFit: "cover",
                                                                borderRadius: "6px",
                                                                border: "1px solid #ddd"
                                                            }}
                                                        />

                                                    ) : (

                                                        <span className="text-muted">
                                                            No Image
                                                        </span>

                                                    )}

                                                </td>


                                                {/* TITLE */}

                                                <td>

                                                    <strong>
                                                        {testimonial.title}
                                                    </strong>

                                                </td>


                                                {/* DESIGNATION */}

                                                <td>
                                                    {testimonial.designation}
                                                </td>


                                                {/* DESCRIPTION */}

                                                <td>

                                                    <div
                                                        style={{
                                                            maxWidth: "350px",
                                                            maxHeight: "80px",
                                                            overflow: "hidden"
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                testimonial.description
                                                        }}
                                                    />

                                                </td>


                                                {/* SORT ORDER */}

                                                <td>
                                                    {testimonial.sort_order}
                                                </td>


                                                {/* ACTION */}

                                                <td>

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-warning me-2"
                                                        onClick={() =>
                                                            handleEdit(
                                                                testimonial.id
                                                            )
                                                        }
                                                        disabled={saving}
                                                        title="Edit"
                                                    >

                                                        <FaEdit />

                                                    </button>


                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() =>
                                                            handleDelete(
                                                                testimonial.id
                                                            )
                                                        }
                                                        disabled={saving}
                                                        title="Delete"
                                                    >

                                                        <FaTrash />

                                                    </button>

                                                </td>

                                            </tr>

                                        ))}

                                    </tbody>

                                </table>

                            </div>

                        )}

                    </div>

                </div>

            )}

        </div>
    );
};

export default Testimonials;
